import Dexie, { Table } from 'dexie';

// Define the types for each table
interface Project {
  id: number;
  name: string;
  permittee: string;
  notes: string;
  routine_interval: string;
  timezone: string;
  inspector_user_id: string;
  document_group: string;
  start_date: string;
  end_date: string;
  inspection_compliance_required: boolean;
  cgp_tracking_id: string;
  next_inspection_date: string;
  archivedAt: string;
  findingComplianceRequired: boolean;
  isCcrProject: boolean;
  precipitation_flag: boolean;
  precipitation_threshold: number;
  snow_threshold: number;
  project_type_id: number;
  inspection_interval_id: number;
  precipitation_edit_lock: boolean;
  primary_contact_user_id: string;
  external_project: string;
  work_order: string;
  purchase_order: string;
  finding_metric_tracking: boolean;
  dissolve_finding_images: boolean;
  enable_photo_metadata_requirement: boolean;
  forecast_weather: string;
  current_acreage: number;
  client: string;
  address: string;
  inspections: any[];
  downloadedOn: string;
  // SW2-2475 — template ids of the forms downloaded to this device. Absent on rows stored before it.
  formsOnDevice?: number[];
}

interface Inspection {
  id: number;
  project_id: number;
  GUID: string;
  template_id: string;
  created_date: string;
  inspection_types: { type: string }[];
  facility_name: string;
  client_name: string;
  cms_inspector_name: string;
  inspector_id: string;
  compliance_id: string;
  municipality: string;
  certification_notes: string;
  certification_date: string;
  acknowledgement_date: string;
  compliance_date: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
  project: any;
  users: any[];
  inspection_template: any;
  findings: any[];
  questions: any[];
  certifications: any[];
  inspectionNumber: number;
  /** Offline queue state. `payload` holds the create request for an inspection made offline. */
  sync?: { synced: boolean; payload?: any };
}

interface User {
  id: number;
  user_id: number;
  /**
   * Scopes the tenant-keyed caches. Not indexed, so it needs no schema version
   * bump — Dexie only requires a migration for changes to the index string.
   */
  tenant_id?: number | null;
  first_name: string;
  last_name: string;
  name: string;
  email: string;
  role: string;
}

// Define your DB structure
interface Database {
  projects: Table<Project, number>; // Table with type 'Project' and primary key 'number'
  inspections: Table<Inspection, number>; // Table with type 'Inspection' and primary key 'number'
  findings: Table<any, number>;
  questions: Table<any, number>;
  certifications: Table<any, number>;
  inspectionNotes: Table<any, number>;
  findingTypeInspectionTemplate: Table<any, number>;
  user: Table<User, number>;
  images: Table<any, number>;
  inspectionUploads: Table<any, number>;
  commentPayload: Table<any, number>;
  inspectionTemplate: Table<any, number>;
  positionables: Table<any, number>;
  projectInspectionUploads: Table<any, number>;
  legendItems: Table<any, number>;
  siteMapUploads: Table<any, number>;
  templateValidations: Table<any, string>;
  clients: Table<any, number>;
  questionConfigValues: Table<any, number>;
  templateQuestionConfigs: Table<any, number>;
  templatePriorityOptions: Table<any, number>;
  enforcementOptions: Table<any, number>;
  clientCustomFields: Table<any, string>;
  tenantFeatures: Table<any, string>;
  mapNotes: Table<any, [number, number]>;
  esriPositionables: Table<any, [number, number]>;
}

const db = new Dexie('SW2OfflineDB') as Dexie & Database;

// Schema-upgrade failure handling. This was previously commented out entirely,
// so a blocked or failed upgrade left every offline feature silently dead with
// no diagnostic and no way for the user to know why.
//
// `blocked` fires when another tab still holds an older version of the database,
// which is common — Dexie then waits indefinitely rather than failing, so
// without this the app just never becomes usable offline.
db.on('blocked', () => {
  console.error(
    'IndexedDB upgrade blocked: another tab has this site open with an older version.',
  );
  window.alert(
    'Offline storage could not be updated because this site is open in another tab. Please close the other tabs and reload this page.',
  );
});

db.on('versionchange', () => {
  // Another tab upgraded the schema. Release our connection so its upgrade can
  // finish; this tab must reload to use the new version.
  console.warn('IndexedDB version changed in another tab; closing connection.');
  db.close();
  window.alert(
    'Offline storage was updated in another tab. This page will now reload to continue working offline.',
  );

  // Reload rather than leaving the tab running against a closed connection.
  // Every later Dexie call rejects with DatabaseClosedError, and the read
  // fallback swallows cache-read failures by design — so the app would silently
  // degrade to network-only with nothing but a console line to show for it.
  window.location.reload();
});

db.version(3).stores({
  projects: `
    id,
    name,
    permittee,
    client_id,
    address_id,
    inspection_template_id,
    notes,
    routine_interval,
    timezone,
    inspector_user_id,
    document_group,
    start_date,
    end_date,
    inspection_compliance_required,
    cgp_tracking_id,
    next_inspection_date,
    archivedAt,
    findingComplianceRequired,
    isCcrProject,
    precipitation_flag,
    precipitation_threshold,
    snow_threshold,
    project_type_id,
    inspection_interval_id,
    precipitation_edit_lock,
    primary_contact_user_id,
    external_project,
    work_order,
    purchase_order,
    finding_metric_tracking,
    dissolve_finding_images,
    enable_photo_metadata_requirement,
    forecast_weather,
    current_acreage,
    client,
    address,
    inspections,
    sync,
    downloadedOn
  `,
  inspections: `
    id,
    project_id,
    GUID,
    template_id,
    created_date,
    inspection_types,
    facility_name,
    client_name,
    cms_inspector_name,
    inspector_id,
    compliance_id,
    municipality,
    certification_notes,
    certification_date,
    acknowledgement_date,
    compliance_date,
    status,
    createdAt,
    updatedAt,
    deletedAt,
    project,
    users,
    inspection_template,
    findings,
    questions,
    certifications,
    inspectionNumber,
    sync
  `,
  findings: `
    id,
    inspection_id,
    location,
    number,
    date_initiated,
    date_completed,
    type,
    condition,
    control_measure,
    completed_by_user_id,
    created_by_user_id,
    finding_type_id,
    initial_inspection_id,
    completed_inspection_id,
    legend_item_id,
    finding_action,
    action_note_id,
    createdAt,
    updatedAt,
    deletedAt,
    InspectionFindingModel,
    commentables,
    finding_uploads,
    action_note,
    legendName,
    sync
  `,
  questions: `
    inspection_id,
    input_label,
    input_attributes,
    input_validation_rules,
    parent_question_id,
    parent_option_id,
    question_type_id,
    sort_order,
    description,
    created_at,
    updated_at,
    deleted_at,
    answer,
    features,
    comment,
    options,
    groupName,
    sync
  `,
  certifications: `
    inspectionId,
    inspectionDOI,
    isCDOT,
    minCertificationDate,
    signatureGroups,
    groupOptions,
    userSignatures,
    attestations,
    sync
  `,
  inspectionNotes: `inspection_id,notes,sync`,
  findingTypeInspectionTemplate: `templateId,findingTypes`,
  user: `id,user_id,first_name,last_name,role,name,email`,
  images: `guid,file,type,name,sync,payload_id,project_id`,
  inspectionUploads: `inspection_id,uploads,sync`,
  commentPayload: `id++,inspection_id,question_id,sync`,
  inspectionTemplate: `template_id,template,certification`,
  positionables: `id,target_id,project_id,sync,start_inspection_id`,
  projectInspectionUploads: `inspection_id,project_id,data`,
  legendItems: `id,project_id,regulation_group_id,name,type`,
});

// v4 caches the reference data the offline inspection flow reads but the
// download previously fetched and discarded. Dexie carries unchanged v3 tables
// forward, so only the additions are declared here.
//
//   siteMapUploads         site-map upload records — the image bytes were already
//                          in `images`, but the GUID/mime metadata needed to
//                          mount a map was network-only
//   templateValidations    per (project, upload); supplies the map's doc type
//   clients                the map editor gates rendering on the client record
//   questionConfigValues   select-one / select-many answer options
//   templateQuestionConfigs / templatePriorityOptions / enforcementOptions
//                          inspection form + finding modal dropdowns
//   clientCustomFields     keyed per (client, field) so any caller's subset hits
//   tenantFeatures         tenantHasFeature() returns false until its query
//                          resolves, which hid every feature-gated control
db.version(4).stores({
  siteMapUploads: `project_id`,
  templateValidations: `key`,
  clients: `id`,
  questionConfigValues: `question_id`,
  templateQuestionConfigs: `template_id`,
  templatePriorityOptions: `template_id`,
  enforcementOptions: `template_id`,
  clientCustomFields: `key`,
  tenantFeatures: `key`,
  // Dropped here and recreated in v5 with a compound primary key — see below.
  // Dexie requires a primary-key change to span two versions.
  legendItems: null,
});

// v5 re-keys legendItems on [project_id+id].
//
// It was keyed on `id` alone while each row is stamped with the project it was
// downloaded for. A legend item shared across projects has one id, so
// downloading project B overwrote project A's row and A's legend items vanished
// offline. The dashboard allows up to 10 projects to be downloaded at once, so
// this was the normal case rather than an edge case.
//
// Records keep their real `id` field — consumers that read item.id (the finding
// form's Legend Item select uses it as the option value) are unaffected.
db.version(5).stores({
  legendItems: `[project_id+id], id, project_id, regulation_group_id, name, type`,
});

// v6 adds mapNotes: the note source models behind the "commentables" positionables placed on a
// site map. Nothing else caches them, so offline prints and map reads had note markers with no
// text. Additive — Dexie creates the store and existing rows are untouched.
db.version(6).stores({
  mapNotes: `[project_id+project_upload_id], project_id`,
});

// v7 adds esriPositionables: the ESRI/ArcGIS features drawn over a georeferenced site map. They
// are vector rows rather than tiles, so one record per map is enough to render them offline.
db.version(7).stores({
  esriPositionables: `[project_id+project_upload_id], project_id`,
});

/** Set once a VersionError reset has been attempted in this tab session. */
const VERSION_RESET_FLAG = 'sw2OfflineVersionReset';

// Must run AFTER every db.version().stores() declaration: opening the database
// first makes Dexie reject any version added afterwards with
// SchemaError: 'Cannot add version when database is open'.
db.open().catch((error: any) => {
  if (error?.name === 'VersionError') {
    // Stored version is NEWER than this build declares — e.g. after a rollback.
    // Recover automatically by discarding the database and reloading.
    //
    // NOTE: this destroys any offline work that had not been uploaded yet
    // (completed inspections, findings, photos, signatures). The data is
    // readable by the newer build, so it is unrecoverable once deleted — rolling
    // the newer build forward will not bring it back. Accepted deliberately in
    // favour of the app self-healing without support involvement.
    console.error(
      'IndexedDB version conflict: stored data is newer than this build. Clearing offline data to recover.',
      error,
    );

    // One attempt per tab session. If another tab running a newer build
    // re-upgrades the database straight after this delete, the reload lands back
    // on the same VersionError — without this flag that is an inescapable
    // alert-and-reload loop with no way to reach the UI and close the other tab.
    if (sessionStorage.getItem(VERSION_RESET_FLAG)) {
      console.error(
        'IndexedDB version conflict persisted after a reset; not retrying.',
      );
      window.alert(
        'Offline features are unavailable: another tab is running a newer version of the app. Please close all other tabs for this site and reload.',
      );

      return;
    }

    Dexie.delete('SW2OfflineDB')
      .then(() => {
        sessionStorage.setItem(VERSION_RESET_FLAG, '1');
        window.alert(
          'Your offline data could not be read by this version of the app and has been reset. The page will now reload. Any inspection work that had not been uploaded will need to be re-entered.',
        );
        window.location.reload();
      })
      .catch((deleteError) => {
        console.error('Failed to clear offline database', deleteError);
        window.alert(
          'Offline features are unavailable and the offline data could not be reset automatically. Please clear this site\'s browser data and reload, or contact support.',
        );
      });

    return;
  }

  if (error?.name === 'QuotaExceededError') {
    console.error('IndexedDB quota exceeded', error);
    window.alert(
      'Your device is out of storage for offline data. Remove a downloaded project to free up space.',
    );

    return;
  }

  console.error('Failed to open IndexedDB', error);
});

export const offlineImageCache: any = {};

// Preload images from IndexedDB into the in-memory cache.
let offlineImagesPreload: Promise<void> | null = null;

// offlineImageCache is in-memory only, so without this rehydration every page
// load loses already-downloaded images. Memoised and returned so callers that
// read the cache (e.g. the map editor) can await readiness instead of racing it.
export const preloadOfflineImages = () => {
  if (!offlineImagesPreload) {
    offlineImagesPreload = db.projects
      .count()
      // With nothing downloaded there is no offline image worth holding, and
      // cleanup clears every table when db.projects is empty — so this skips
      // reading (and permanently retaining) the whole image table for the users
      // who never go offline at all. Cannot be gated on `navigator.onLine`
      // instead: that reports true after an offline reload, which is precisely
      // when the rehydration is needed.
      .then((projectCount) => (projectCount === 0 ? null : db.images.toArray()))
      .then((images) => {
        (images || []).forEach((image) => {
          offlineImageCache[image.guid] = image.file;
        });
      })
      .catch((error) => {
        console.error('Error preloading offline images:', error);
      });
  }

  return offlineImagesPreload;
};

export const downloadAndStoreImage = (
  imageUrl: string,
  guid: string,
  name: string,
  type: string,
  project_id: number,
) => {
  // Must return the promise — callers push this into arrays they then
  // `await Promise.allSettled(...)` on. Without it they await an array of
  // `undefined`, which resolves instantly and never waits for the images.
  return fetch(imageUrl)
    .then((response) => {
      if (response.ok) {
        return response.blob();
      } else {
        throw new Error(`Failed to fetch image from S3: ${imageUrl}`);
      }
    })
    .then((blob) => {
      // Convert blob to Base64 string
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => reject('Failed to convert blob to Base64');
        reader.readAsDataURL(blob);
      });
    })
    .then((base64Image) => {
      // Save the image data to IndexedDB
      offlineImageCache[guid] = base64Image;

      return db.images.put({
        guid, // Provided GUID, or you can generate one using crypto.randomUUID()
        file: base64Image, // The Base64 image data
        type, // e.g., 'image/jpeg' or 'image/png'
        name, // e.g., 'Fuel_Sep_4.png'
        sync: { synced: true },
        project_id: project_id,
      });
    })
    .catch((error) => {
      console.error('Error downloading or storing image:', error);
    });
};

export const templateValidationKey = (
  projectId: number | string,
  uploadGuid: string,
) => `${Number(projectId)}:${uploadGuid}`;

/** Site-map upload records for a project, as returned by uploadApi.project.index. */
/**
 * Cached site-map uploads for a project.
 *
 * `projectDocTypeId` matters: the online call filters by a single doc type, but
 * the download stores uploads for every doc type under 'Active Site Maps' (and
 * the API applies no filter at all when handed an array of ids — see
 * projectUploadController.index). Reading unfiltered therefore returned uploads
 * from doc types the caller did not ask for whenever a project had sub-doc-types.
 */
export const getOfflineSiteMapUploads = async (
  projectId: number,
  projectDocTypeId?: number | null,
) => {
  const row = await db.siteMapUploads.get(Number(projectId));
  const data = row?.data ?? null;

  if (!data || projectDocTypeId == null) {
    return data;
  }

  return data.filter(
    (upload: any) =>
      Number(upload.project_doc_type_id) === Number(projectDocTypeId),
  );
};

/**
 * Minimal projectDocs shape cached for offline use: only the 'Active Site Maps'
 * document type. The full /docs payload scales with every upload a project has
 * ever had (~460KB on a document-heavy project) and offline consumers only read
 * this one type, so caching the whole tree would be a poor trade.
 */
/** ESRI features cached for one site map, or null when the map has none. */
export const getOfflineEsriPositionables = async (
  projectId: number,
  projectUploadId: number,
) => {
  const row = await db.esriPositionables.get([
    Number(projectId),
    Number(projectUploadId),
  ]);

  return row?.data ?? null;
};

export const getOfflineProjectDocs = async (projectId: number) => {
  const row = await db.siteMapUploads.get(Number(projectId));

  return row?.docs ?? null;
};

/**
 * Question types cached alongside the downloaded inspection template. They are
 * global rather than per-template, so the first stored copy is fine. Without
 * this the inspection details layout never finishes loading offline: it gates
 * rendering on questionTypesQuery having fetched.
 */
export const getOfflineQuestionTypes = async () => {
  // Stops at the first row that carries them rather than materialising every
  // cached template. Each row holds a full question_groups tree, so a `toArray()`
  // here deserialised the entire template cache to read one small array.
  //
  // Still deserialises the rows it walks. Giving question types their own
  // single-row table would avoid that entirely, but that needs a schema version
  // bump and this branch already carries an untested v4->v5 migration — see
  // SW2-2224-frontend-review-response.md (P4, M2).
  let questionTypes: any = null;

  await db.inspectionTemplate.each((row: any) => {
    if (!questionTypes && row?.questionTypes?.length) {
      questionTypes = row.questionTypes;
    }
  });

  return questionTypes;
};

/**
 * Reference data behind the inspection form's and finding modal's dropdowns.
 * Each reader returns null when nothing is cached so callers can fall through to
 * the network rather than render an empty select.
 */
export const getOfflineQuestionConfigValues = async (questionId: number) => {
  const row = await db.questionConfigValues.get(Number(questionId));

  return row?.data ?? null;
};

export const getOfflineTemplateQuestionConfig = async (templateId: number) => {
  const row = await db.templateQuestionConfigs.get(Number(templateId));

  return row?.data ?? null;
};

export const getOfflineTemplatePriorityOptions = async (templateId: number) => {
  const row = await db.templatePriorityOptions.get(Number(templateId));

  return row?.data ?? null;
};

export const getOfflineEnforcementOptions = async (templateId: number) => {
  const row = await db.enforcementOptions.get(Number(templateId));

  return row?.data ?? null;
};

export const getOfflineFindingTypes = async (templateId: number) => {
  // Written by the download as { templateId, findingTypes }.
  const row = await db.findingTypeInspectionTemplate.get(Number(templateId));

  return row?.findingTypes ?? null;
};

// Keyed per (client, field) rather than per field-set: callers request differing
// subsets (the finding form asks for four fields, the download fetched three), and
// keying on the whole set meant any mismatch was a cache miss.
/**
 * Which tenant the cached rows below belong to.
 *
 * These caches live in one IndexedDB per browser profile, but a user can hold
 * roles in several tenants and a field tablet can be shared. Keyed without a
 * tenant, whichever download ran last won: feature-gated controls rendered
 * against another tenant's flags, and another org's client settings were
 * readable. Because the read fallback also fires on any failed online request,
 * that was not confined to offline mode.
 *
 * Set eagerly at login (AppContext) and also persisted on the `user` singleton,
 * because after an offline reload the profile request cannot resolve and the
 * in-memory value would be lost — which would otherwise re-break the very thing
 * this ticket fixed, feature flags being invisible offline.
 */
let currentTenantScope: number | null = null;

export const setOfflineTenantScope = (tenantId: number | string | null) => {
  currentTenantScope = Number(tenantId) || null;
};

const resolveTenantScope = async (): Promise<number | null> => {
  if (currentTenantScope != null) {
    return currentTenantScope;
  }

  try {
    const row: any = await db.user.get(1);
    const persisted = Number(row?.tenant_id) || null;

    currentTenantScope = persisted;

    return persisted;
  } catch {
    return null;
  }
};

export const clientCustomFieldsKey = (
  tenantId: number | string | null,
  clientId: number | string,
  field: string,
) => `${Number(tenantId) || 'unknown'}:${Number(clientId)}:${field}`;

const tenantFeaturesKey = (tenantId: number | string | null) =>
  `${Number(tenantId) || 'unknown'}:enabled`;

export const putOfflineTenantFeatures = async (features: any) => {
  const tenantId = await resolveTenantScope();

  await db.tenantFeatures.put({ key: tenantFeaturesKey(tenantId), data: features });
};

export const getOfflineTenantFeatures = async () => {
  const tenantId = await resolveTenantScope();

  // Fails closed when the tenant is unknown rather than reading a row that may
  // belong to a different one. A null here means "no cached answer", which
  // callers already treat as a cache miss.
  if (tenantId == null) {
    return null;
  }

  const row = await db.tenantFeatures.get(tenantFeaturesKey(tenantId));

  return row?.data ?? null;
};

/**
 * SW2-2475 — the last server answer for "which projects has this user downloaded", so the
 * dashboard can list downloads offline. Kept in the tenantFeatures key/value table rather than a
 * new one, to avoid a schema version bump (a VersionError on upgrade clears the database).
 */
const downloadsListKey = (tenantId: number | string | null, userId: number) =>
  `${Number(tenantId) || 'unknown'}:offline-downloads:${Number(userId)}`;

export const putOfflineDownloadsList = async (userId: number, data: any) => {
  const tenantId = await resolveTenantScope();

  await db.tenantFeatures.put({ key: downloadsListKey(tenantId, userId), data });
};

export const getOfflineDownloadsList = async (userId: number) => {
  const tenantId = await resolveTenantScope();

  if (tenantId == null) return null;

  const row = await db.tenantFeatures.get(downloadsListKey(tenantId, userId));

  return row?.data ?? null;
};

/** Every custom field any caller asks for, so one download covers them all. */
export const CLIENT_CUSTOM_FIELDS = [
  'finding_metric_tracking',
  'disable_images',
  'enable_photo_metadata_requirement',
  'show_observation_dates_of_findings',
  'require_closure_photos',
];

export const putOfflineClientCustomFields = async (
  clientId: number,
  data: Record<string, any>,
) => {
  const tenantId = await resolveTenantScope();
  const rows = Object.keys(data || {}).map((field) => ({
    key: clientCustomFieldsKey(tenantId, clientId, field),
    tenant_id: tenantId,
    client_id: Number(clientId),
    field,
    value: data[field],
  }));

  if (!rows.length) {
    return;
  }

  await db.clientCustomFields.bulkPut(rows);
};

export const getOfflineClientCustomFields = async (
  clientId: number,
  fields: string[],
) => {
  const tenantId = await resolveTenantScope();

  // Fails closed rather than reading another tenant's cached settings.
  if (tenantId == null) {
    return null;
  }

  const rows = await db.clientCustomFields.bulkGet(
    fields.map((field) => clientCustomFieldsKey(tenantId, clientId, field)),
  );
  const found = rows.filter(Boolean) as any[];

  if (!found.length) {
    return null;
  }

  // Assemble whatever is cached. A missing field reads as undefined, which is
  // what a failed network call would have produced anyway — so a partial answer
  // is strictly better than none.
  return found.reduce((acc: Record<string, any>, row: any) => {
    acc[row.field] = row.value;

    return acc;
  }, {});
};

/** Cached inspection-template validation for a (project, upload) pair. */
export const getOfflineTemplateValidation = async (
  projectId: number,
  uploadGuid: string,
) => {
  const row = await db.templateValidations.get(
    templateValidationKey(projectId, uploadGuid),
  );

  return row?.data ?? null;
};

const isUnsynced = (record: any) =>
  Boolean(record?.sync) && record.sync.synced === false;

/**
 * `images` holds two kinds of row under one `payload_id`: finding photos (keyed
 * by finding id) and inspection attachments (keyed by inspection id). The ids
 * come from different sequences and can be equal, so tell them apart by the
 * inspection_id an attachment queues (InspectionDocumentUploads.tsx).
 */
export const isInspectionAttachment = (image: any) =>
  image?.sync?.payload?.inspection_id !== undefined;

/**
 * How many rows of this project's offline work have not reached the server yet?
 *
 * Everything an inspector can produce offline is counted: inspections created
 * offline, answers, findings, certifications (signatures), positionables,
 * photos and attachments, notes and comments.
 *
 * SW2-2443: the offline sync reads this before it deletes the project's local
 * copy. It used to delete unconditionally, so work that failed to upload — a
 * connection dropped mid-sync — was destroyed while the UI said "synced".
 *
 * Throws if a table cannot be read; callers must treat that as unsynced work.
 */
export const countUnsyncedWork = async (projectId: number): Promise<number> => {
  const inspections = await db.inspections
    .where('project_id')
    .equals(projectId)
    .toArray();
  const inspectionIds = inspections.map((i: Inspection) => i.id);

  let count = inspections.filter(isUnsynced).length;

  // Scoped by project_id directly.
  const [positionables, projectInspectionUploads, projectImages] =
    await Promise.all([
      db.positionables.where('project_id').equals(projectId).toArray(),
      db.projectInspectionUploads.where('project_id').equals(projectId).toArray(),
      db.images.where('project_id').equals(projectId).toArray(),
    ]);

  count += [...positionables, ...projectInspectionUploads].filter(isUnsynced)
    .length;

  // Keyed by guid: an image can be reached by project_id and by payload_id.
  const images = new Map<string, any>();

  projectImages.forEach((img: any) => images.set(img.guid, img));

  if (!inspectionIds.length) {
    return count + Array.from(images.values()).filter(isUnsynced).length;
  }

  // A finding is reachable two ways: its inspection_id, and the inspection's
  // own findings list (which the sync walks). Take both, once each.
  const listedFindingIds = inspections
    .map((i: Inspection) => (i.findings || []).map((f: any) => f.id))
    .flat();
  const [byInspectionId, listed] = await Promise.all([
    db.findings.where('inspection_id').anyOf(inspectionIds).toArray(),
    db.findings.bulkGet(listedFindingIds),
  ]);
  const findings = new Map<number, any>();

  [...byInspectionId, ...listed]
    .filter(Boolean)
    .forEach((f: any) => findings.set(f.id, f));

  count += Array.from(findings.values()).filter(isUnsynced).length;

  // Keyed by inspection id. `where(...).anyOf(...)` rather than `.filter(...)`:
  // inspection_id is indexed on both tables, and this runs at module load
  // contending with the rest of first paint for IndexedDB transactions.
  const byInspection = await Promise.all([
    db.questions.bulkGet(inspectionIds),
    db.certifications.bulkGet(inspectionIds),
    db.inspectionNotes.bulkGet(inspectionIds),
    db.inspectionUploads.bulkGet(inspectionIds),
    db.commentPayload.where('inspection_id').anyOf(inspectionIds).toArray(),
  ]);

  count += byInspection
    .map((rows: any[]) => (rows || []).filter(Boolean))
    .flat()
    .filter(isUnsynced).length;

  // Queued photos and attachments are written without a project_id — they are
  // keyed by the finding or inspection they belong to — so the project_id
  // lookup above never found them.
  const inspectionIdSet = new Set(inspectionIds);
  const findingIdSet = new Set(Array.from(findings.keys()));
  const attached = await db.images
    .where('payload_id')
    .anyOf([...inspectionIds, ...Array.from(findingIdSet)])
    .toArray();

  attached
    .filter((img: any) =>
      isInspectionAttachment(img)
        ? inspectionIdSet.has(img.payload_id)
        : findingIdSet.has(img.payload_id),
    )
    .forEach((img: any) => images.set(img.guid, img));

  return count + Array.from(images.values()).filter(isUnsynced).length;
};

/**
 * Does this project hold work that has not been pushed to the server yet?
 *
 * Used to protect a project from automatic cleanup. Errs on the side of "yes":
 * if a table cannot be read we report unsynced work rather than allow a delete
 * we cannot justify.
 */
export const hasUnsyncedWork = async (projectId: number): Promise<boolean> => {
  try {
    return (await countUnsyncedWork(projectId)) > 0;
  } catch (error) {
    console.error(
      `Could not determine sync state for project ${projectId}; treating it as unsynced.`,
      error,
    );

    return true;
  }
};

/**
 * Move an inspection created offline, and everything recorded against it, from
 * its local id to the id the server gave it.
 *
 * SW2-2443: the sync used to re-key only the inspection row, then re-key each
 * child as it synced. A child that failed stayed under the local id, and the
 * next sync — which now saw a synced inspection under the server id — looked
 * for its children under the server id and never found them. Re-keying
 * everything up front, in one transaction, lets a retry pick up whatever is
 * left.
 *
 * Findings keep their own local ids: they are looked up through the
 * inspection's findings list, and the sync records their server id separately.
 */
export const rekeyOfflineInspection = async (oldId: number, newId: number) => {
  await db.transaction(
    'rw',
    [
      db.projects,
      db.inspections,
      db.questions,
      db.certifications,
      db.inspectionNotes,
      db.inspectionUploads,
      db.projectInspectionUploads,
      db.commentPayload,
      db.findings,
      db.images,
      db.positionables,
    ],
    async () => {
      const inspection = await db.inspections.get(oldId);

      if (!inspection) {
        throw new Error(`Offline inspection ${oldId} not found`);
      }

      // Local ids are `latest.id + 100` (inspection.js), inside the server's
      // range, so the new id can already be taken locally. Overwriting that row
      // would silently replace another inspection and its answers and signatures.
      if (newId !== oldId && (await db.inspections.get(newId))) {
        throw new Error(
          `Cannot move offline inspection ${oldId} to ${newId}: that id is already in use on this device`,
        );
      }

      await db.inspections.delete(oldId);
      await db.inspections.put({
        ...inspection,
        id: newId,
        sync: { synced: true },
      });

      // Tables whose primary key is the inspection id: delete and re-add.
      const moveRow = async (table: any, keyField: string) => {
        const row = await table.get(oldId);

        if (row) {
          await table.delete(oldId);
          await table.put({ ...row, [keyField]: newId });
        }
      };

      await moveRow(db.questions, 'inspection_id');
      await moveRow(db.certifications, 'inspectionId');
      await moveRow(db.inspectionNotes, 'inspection_id');
      await moveRow(db.inspectionUploads, 'inspection_id');
      await moveRow(db.projectInspectionUploads, 'inspection_id');

      await db.commentPayload
        .where('inspection_id')
        .equals(oldId)
        .modify((c: any) => {
          c.inspection_id = newId;
        });

      await db.findings
        .where('inspection_id')
        .equals(oldId)
        .modify((f: any) => {
          f.inspection_id = newId;

          if (f.InspectionFindingModel) {
            f.InspectionFindingModel.inspection_id = newId;
          }
        });

      // Queued finding writes carry the inspection they were made on. That
      // includes edits to findings carried over from an earlier inspection,
      // whose own inspection_id is not this one — so walk the findings list.
      const findingIds = (inspection.findings || []).map((f: any) => f.id);

      await db.findings
        .where('id')
        .anyOf(findingIds)
        .modify((f: any) => {
          if (!Array.isArray(f.sync?.payload)) return;

          f.sync.payload = f.sync.payload.map((entry: any) =>
            Number(entry.inspectionId) === oldId
              ? { ...entry, inspectionId: newId }
              : entry,
          );
        });

      await db.images
        .where('payload_id')
        .equals(oldId)
        .filter(isInspectionAttachment)
        .modify((img: any) => {
          img.payload_id = newId;
          img.sync.payload.inspection_id = newId;
        });

      // Both ends of a placement's inspection window: map saves set
      // end_inspection_id to the next inspection (MapEditor.js), which can be
      // this one while it is still local.
      const moveWindow = (row: any) => {
        if (row.start_inspection_id === oldId) row.start_inspection_id = newId;
        if (row.end_inspection_id === oldId) row.end_inspection_id = newId;
      };

      await db.positionables
        .where('project_id')
        .equals(inspection.project_id)
        .filter(
          (p: any) =>
            p.start_inspection_id === oldId ||
            p.end_inspection_id === oldId ||
            (p.sync?.payload || []).some(
              (e: any) =>
                e.start_inspection_id === oldId || e.end_inspection_id === oldId,
            ),
        )
        .modify((p: any) => {
          moveWindow(p);
          p.sync.payload = (p.sync.payload || []).map((entry: any) => {
            const moved = { ...entry };

            moveWindow(moved);

            return moved;
          });
        });

      const project = await db.projects.get(inspection.project_id);

      if (project) {
        await db.projects.update(inspection.project_id, {
          inspections: (project.inspections || []).map((i: any) =>
            i.id === oldId ? { ...i, id: newId } : i,
          ),
        });
      }
    },
  );
};

/**
 * Move a finding created offline, and every local reference to it, from its
 * local id to the id the server gave it — and drop the create that just landed
 * from its queue, in the same transaction.
 *
 * SW2-2443: the finding used to stay under its local id while its placements
 * were pointed at the server id. A partial sync keeps the local copy, so that
 * split showed up: the offline map resolves a placement's finding by
 * positionable_id and found nothing, and a later offline edit — which rewrites
 * the finding's whole `sync` object — lost the server id, so the next sync sent
 * the edit to the local id and was refused every time.
 */
export const rekeyOfflineFinding = async (
  projectId: number,
  oldId: number,
  newId: number,
) => {
  await db.transaction(
    'rw',
    [db.findings, db.inspections, db.positionables, db.images],
    async () => {
      const finding = await db.findings.get(oldId);

      if (!finding) {
        throw new Error(`Offline finding ${oldId} not found`);
      }

      if (newId !== oldId && (await db.findings.get(newId))) {
        throw new Error(
          `Cannot move offline finding ${oldId} to ${newId}: that id is already in use on this device`,
        );
      }

      const queue = (finding.sync?.payload || []).slice(1);

      await db.findings.delete(oldId);
      await db.findings.put({
        ...finding,
        id: newId,
        sync: { ...finding.sync, payload: queue, serverId: newId },
      });

      // Every inspection that lists it: the one it was made on, and any it was
      // carried into.
      const inspections = await db.inspections
        .where('project_id')
        .equals(projectId)
        .toArray();

      for (const inspection of inspections) {
        if (!(inspection.findings || []).some((f: any) => f.id === oldId)) {
          continue;
        }

        await db.inspections.update(inspection.id, {
          findings: inspection.findings.map((f: any) =>
            f.id === oldId ? { ...f, id: newId } : f,
          ),
        });
      }

      await db.positionables
        .where('project_id')
        .equals(projectId)
        .filter(
          (p: any) =>
            p.positionable_id === oldId &&
            (p.positionable_type ||
              p.sync?.payload?.[0]?.positionable_type) === 'findings',
        )
        .modify((p: any) => {
          p.positionable_id = newId;
          p.sync.payload = (p.sync.payload || []).map((entry: any) => ({
            ...entry,
            positionable_id: newId,
          }));
        });

      await db.images
        .where('payload_id')
        .equals(oldId)
        .filter((img: any) => !isInspectionAttachment(img))
        .modify((img: any) => {
          img.payload_id = newId;

          if (img.sync?.payload?.payload_id !== undefined) {
            img.sync.payload.payload_id = newId;
          }
        });
    },
  );
};

/**
 * Clear every offline cache. Called on sign-out so a shared device does not
 * retain the previous user's client records, custom fields, tenant features,
 * project docs, site maps, legend items and cached images.
 *
 * Refuses to clear anything while ANY downloaded project still has unsynced
 * work. An inspector's unsent findings are not ours to discard on a logout, and
 * a partial clear is worse than none — it would orphan rows the sync needs. The
 * caller gets the retained project ids so it can say something useful.
 *
 * Whether a forced clear should be offered (with a warning) is an open product
 * question — see SW2-2224-frontend-review-response.md.
 */
export const clearOfflineData = async (): Promise<{
  cleared: boolean;
  retainedProjectIds: number[];
}> => {
  try {
    const projects = await db.projects.toArray();
    const retainedProjectIds: number[] = [];

    for (const project of projects) {
      if (await hasUnsyncedWork(project.id)) {
        retainedProjectIds.push(project.id);
      }
    }

    if (retainedProjectIds.length) {
      return { cleared: false, retainedProjectIds };
    }

    await Promise.all(db.tables.map((table) => table.clear()));
    currentTenantScope = null;

    return { cleared: true, retainedProjectIds: [] };
  } catch (error) {
    console.error('Could not clear offline data', error);

    return { cleared: false, retainedProjectIds: [] };
  }
};

/**
 * The forms a download made before `formsOnDevice` existed covers: its inspections' forms whose
 * template data is stored. An inspection alone doesn't show a form was downloaded — the download
 * stores the project's latest inspection whatever its form (PR #151 review). Template data is kept
 * per form, not per project, so a form downloaded for another project still counts.
 */
const legacyFormsOnDevice = async (projectId: number): Promise<number[]> => {
  const inspected: number[] = Array.from(
    new Set<number>(
      (await db.inspections.where('project_id').equals(projectId).toArray())
        .map((i: any) => Number(i.template_id))
        .filter(Boolean),
    ),
  );
  const templates = await db.inspectionTemplate.bulkGet(inspected);

  return inspected.filter((_, i) => templates[i]);
};

/**
 * SW2-2475 — store a downloaded project, keeping the forms already recorded for it (a re-download,
 * or another form's download, must not lose them). For a download made before `formsOnDevice`
 * existed, they are read from its inspections, keeping forms whose template data is stored. One
 * transaction: two tabs downloading forms of one project would otherwise each overwrite the other's
 * list (PR #151 review).
 */
export const putDownloadedProject = (project: any, fields: Record<string, any>) =>
  db.transaction('rw', db.projects, db.inspections, db.inspectionTemplate, async () => {
    const prev: any = await db.projects.get(project.id);
    const formsOnDevice = prev
      ? prev.formsOnDevice ?? (await legacyFormsOnDevice(project.id))
      : [];

    await db.projects.put({ ...project, ...fields, formsOnDevice });
  });

/**
 * SW2-2459 — store a downloaded inspection's certification, but not over signatures queued on this
 * device: the server copy lacks them, and `index` shows them as if signed (as for queued answers).
 * Checked and written in one transaction, and a row that can't be read is kept, not overwritten:
 * either way a queued signature was lost, and its report never sent (PR #156 review). Resolves
 * false when it kept the device's row.
 */
export const putDownloadedCertification = (certifications: any): Promise<boolean> =>
  db.transaction('rw', db.certifications, async () => {
    const inspectionId = Number(certifications.inspectionId);
    const local: any = await db.certifications.get(inspectionId);

    if (local?.sync?.synced === false) {
      // eslint-disable-next-line no-console
      console.warn(`Kept queued signatures for inspection ${inspectionId}; skipping certification overwrite.`);

      return false;
    }

    await db.certifications.put({ ...certifications, inspectionId, sync: { synced: true, payload: [] } });

    return true;
  });

/** Record a form as on this device — called only once its offline data is stored. */
export const recordFormOnDevice = (projectId: number, templateId: number) =>
  db.transaction('rw', db.projects, async () => {
    const row: any = await db.projects.get(Number(projectId));

    // Not stored: the download did not succeed, and must not be reported as if it did.
    if (!row) throw new Error(`Project ${projectId} is not stored on this device`);

    await db.projects.update(Number(projectId), {
      formsOnDevice: Array.from(
        new Set<number>([...(row.formsOnDevice || []), Number(templateId)]),
      ).filter(Boolean),
    });
  });

/**
 * SW2-2475 — remove a download only if nothing of it is waiting to sync, checked and deleted in one
 * transaction over every table. Checked separately, another tab could queue work (a note, a
 * signature) between the check and the delete, and the delete took it (PR #151 review).
 */
export const removeDownloadIfSynced = async (
  rawProjectId: number,
  // SW2-2459: a reason to remove it that must still hold when it goes, decided in the same
  // transaction (e.g. the auto-clear: still this user's, still due — PR #156 review).
  stillWanted: (project: any) => boolean = () => true,
): Promise<boolean> => {
  const projectId = Number(rawProjectId);

  return db.transaction('rw', db.tables, async () => {
    const project = await db.projects.get(projectId);

    if (project && !stillWanted(project)) return false;
    if ((await countUnsyncedWork(projectId)) > 0) return false;

    await removeOfflineProjects(projectId);

    return true;
  });
};

export const removeOfflineProjects = async (rawProjectId: number) => {
  // Callers pass this straight through from route params and component props, so
  // it can arrive as a string. Every writer stores numbers and IndexedDB keys are
  // type-sensitive, so an uncoerced id here deletes nothing and reports success —
  // leaving the project's cached data behind after the user removed it.
  const projectId = Number(rawProjectId);

  try {
    await db.projects.delete(projectId);

    const inspection = await db.inspections
      .where('project_id')
      .equals(projectId)
      .toArray();

    await db.inspections.bulkDelete(inspection.map((i: Inspection) => i.id));
    await db.findings.bulkDelete(
      inspection
        .map((i: Inspection) => i.findings.map((f: any) => f.id))
        .flat(),
    );
    await db.questions.bulkDelete(inspection.map((i: Inspection) => i.id));
    await db.certifications.bulkDelete(inspection.map((i: Inspection) => i.id));
    await db.images.where('project_id').equals(projectId).delete();
    await db.inspectionNotes.bulkDelete(
      inspection.map((i: Inspection) => i.id),
    );
    await db.inspectionUploads.bulkDelete(
      inspection.map((i: Inspection) => i.id),
    );
    await db.commentPayload.bulkDelete(inspection.map((i: Inspection) => i.id));
    await db.positionables.where('project_id').equals(projectId).delete();
    await db.projectInspectionUploads
      .where('project_id')
      .equals(projectId)
      .delete();
    await db.legendItems.where('project_id').equals(projectId).delete();
    await db.siteMapUploads.delete(projectId);
    await db.mapNotes.where('project_id').equals(projectId).delete();
    await db.esriPositionables.where('project_id').equals(projectId).delete();
    await db.templateValidations
      .filter((row: any) => row.project_id === projectId)
      .delete();
  } catch (e) {
    console.log('error', e);
    throw e;
  }
};

const deleteOldOfflineProjects = async (daysToKeep = 3) => {
  try {
    const now = Date.now();
    const threshold = now - daysToKeep * 24 * 60 * 60 * 1000;

    const projects = await db.projects.toArray();

    if (!projects || projects.length === 0) {
      console.log('No projects found, clearing all data');

      const getAllTables = await db.tables;

      await Promise.all(
        getAllTables.map((table) => {
          return table.clear();
        }),
      );
    }

    const oldProjects = await db.projects
      .filter((project) => {
        const downloaded = new Date(project.downloadedOn).getTime();

        return downloaded < threshold;
      })
      .toArray();

    for (const project of oldProjects) {
      // This cleanup previously ignored sync state, so an inspection completed
      // offline but not yet uploaded was cascade-deleted on app start once its
      // project passed the age cut-off — losing the work before the inspector
      // ever had a chance to sync. Never delete a project with pending work; it
      // will be cleaned up on a later run once it has been uploaded.
      if (await hasUnsyncedWork(project.id)) {
        console.warn(
          `Keeping offline project "${project.name}" (${project.id}) past the ${daysToKeep}-day cut-off: it has unsynced work.`,
        );

        continue;
      }

      console.log(`Deleting offline project: ${project.name}`);
      await removeOfflineProjects(project.id); // your existing cascade delete
    }
  } catch (error) {
    console.error('Error deleting old offline projects:', error);
    // Don't throw - this is a background cleanup task
  }
};

// Run cleanup with error handling
deleteOldOfflineProjects().catch((error) => {
  console.error('Failed to run offline project cleanup:', error);
});

// The preload is kicked off unconditionally from src/index.js — `navigator.onLine`
// reports true after an offline reload, so gating on it here would skip the
// rehydration in exactly the case that needs it. This duplicate call is therefore
// redundant; preloadOfflineImages is memoised, but calling it twice at module load
// only added contention for the same IndexedDB transactions during first paint.

export default db;
